// Renders the WHP 198 audio description data channel as PCM samples.
//
// Packets are sent back-to-back for the whole programme, 10 per second, so
// downstream decoders stay locked; outside description passages they carry
// a fade of 0. Each bit is Manchester coded (§4): a 0 is a low-to-high
// transition at mid-bit and a 1 is high-to-low. Levels are +/-0x0200 in
// 16-bit words (-36 dBFS, §5) and every transition is shaped with a
// raised-cosine edge. Half-bit edges sit at exact fractional sample
// positions, so 37/38-sample bits at 48 kHz never drift.

import {
    BIT_RATE,
    PACKET_BITS,
    PACKETS_PER_SECOND,
    LEVEL_16BIT,
    DEFAULT_SAMPLE_RATE,
    EDGE_WIDTH_SAMPLES,
} from "./constants.js";
import { buildAdDescriptor, dbToFadeByte } from "./descriptor.js";
import { buildPassages, createFadeLookup } from "./envelope.js";

const HALF_BITS_PER_PACKET = PACKET_BITS * 2;

/**
 * @typedef {object} ControlTrackOptions
 * @property {Array<{start: number, end: number}>} events - AD events, seconds
 * @property {number} durationSec - programme duration in seconds
 * @property {number} [fadeDb] - attenuation during descriptions, e.g. -12
 * @property {number} [fadeInMs]
 * @property {number} [fadeOutMs]
 * @property {number} [pan] - AD_pan_byte, 0 = centre
 * @property {number} [sampleRate]
 */

/**
 * Creates a renderer that produces the control track as 16-bit samples.
 *
 * @param {ControlTrackOptions} options
 */
export function createControlTrackRenderer({
    events,
    durationSec,
    fadeDb = -12,
    fadeInMs = 200,
    fadeOutMs = 200,
    pan = 0,
    sampleRate = DEFAULT_SAMPLE_RATE,
}) {
    const fadeByte = dbToFadeByte(fadeDb);
    const passages = buildPassages(events, { fadeInMs, fadeOutMs });
    const fadeAt = createFadeLookup(passages, { fadeInMs, fadeOutMs });
    const halfBitSamples = sampleRate / (BIT_RATE * 2);
    const halfEdge = EDGE_WIDTH_SAMPLES / 2;
    const totalSamples = Math.round(durationSec * sampleRate);

    let packetIndex = -1;
    let packet = null;
    const fadeBytes = [];

    // Fade value carried by packet p. A decoder can act on a packet only once
    // it has been fully received, so each packet carries the value for the
    // time its last bit arrives.
    function packetFadeByte(p) {
        return Math.round(fadeAt((p + 1) / PACKETS_PER_SECOND) * fadeByte);
    }

    // +1 (high) or -1 (low) for half-bit k
    function level(k) {
        if (k < 0) k = 0;
        const p = Math.floor(k / HALF_BITS_PER_PACKET);
        if (p !== packetIndex) {
            packetIndex = p;
            const fade = packetFadeByte(p);
            fadeBytes[p] = fade;
            packet = buildAdDescriptor({ fade, pan });
        }
        const within = k - p * HALF_BITS_PER_PACKET;
        const bitIndex = within >> 1;
        const bit = (packet[bitIndex >> 3] >> (7 - (bitIndex & 7))) & 1;
        const firstHalf = (within & 1) === 0;
        // bit 1: high then low; bit 0: low then high
        return (bit === 1) === firstHalf ? 1 : -1;
    }

    function sampleAt(n) {
        const edge = Math.round(n / halfBitSamples);
        const offset = n - edge * halfBitSamples;
        if (edge > 0 && Math.abs(offset) < halfEdge) {
            const before = level(edge - 1);
            const after = level(edge);
            if (before !== after) {
                const t = (offset + halfEdge) / EDGE_WIDTH_SAMPLES;
                const shaped = before + (after - before) * 0.5 * (1 - Math.cos(Math.PI * t));
                return Math.round(shaped * LEVEL_16BIT);
            }
        }
        return level(Math.floor(n / halfBitSamples)) * LEVEL_16BIT;
    }

    return {
        sampleRate,
        totalSamples,
        fadeByte,
        passages,
        /** Fade byte sent in each packet rendered so far, by packet index. */
        fadeBytes,
        /**
         * Yields consecutive Int16Array chunks covering the whole programme.
         * @param {number} [chunkSamples]
         */
        *chunks(chunkSamples = sampleRate * 10) {
            for (let start = 0; start < totalSamples; start += chunkSamples) {
                const length = Math.min(chunkSamples, totalSamples - start);
                const chunk = new Int16Array(length);
                for (let i = 0; i < length; i++) {
                    chunk[i] = sampleAt(start + i);
                }
                yield chunk;
            }
        },
    };
}

/**
 * Renders the whole control track into one Int16Array. Convenient for tests
 * and short programmes; use createControlTrackRenderer().chunks() for long
 * ones.
 *
 * @param {ControlTrackOptions} options
 * @returns {Int16Array}
 */
export function renderControlTrack(options) {
    const renderer = createControlTrackRenderer(options);
    const out = new Int16Array(renderer.totalSamples);
    let offset = 0;
    for (const chunk of renderer.chunks()) {
        out.set(chunk, offset);
        offset += chunk.length;
    }
    return out;
}
